import { useMemo } from "react";

import type { AutopilotState, ChatMessage, ChatTask } from "@/sdk/agent/chat";

export type SeparatorSubType = "chat-ended" | "agent-joined" | "autopilot-active";

export type MessageListItemType =
    | { type: "message"; data: ChatMessage; index: number }
    | { type: "typing"; data: { sessionId: string; customerName?: string; isLastCustomerMessage: boolean } }
    | { type: "separator"; data: { chatTask: ChatTask; separatorType: SeparatorSubType } };

// Helper to safely parse a date value (handles Date, string, or Unix timestamp)
function toDate(val: string | Date | number | undefined | null): Date | null {
    if (!val) return null;
    if (val instanceof Date) return val;
    return new Date(val);
}

/**
 * Prepares a unified list of all items to display in the message list,
 * including regular messages, typing indicators, and separators
 */
export function useMessageListItems(
    messages: ChatMessage[],
    chatTask: ChatTask,
    isCustomerTyping: boolean,
    sessionId: string,
    customerName?: string,
    autopilotState?: AutopilotState,
): MessageListItemType[] {
    return useMemo(() => {
        const items: MessageListItemType[] = [];
        if (!messages || messages.length === 0) {
            // No messages - show agent-joined and chat-ended separators if needed for edge case scenarios
            const agentJoinedAt = toDate(chatTask.assignedAt);
            if (agentJoinedAt) {
                items.push({ type: "separator", data: { chatTask, separatorType: "agent-joined" } });
            }
            if (chatTask.status === "wrapping") {
                items.push({ type: "separator", data: { chatTask, separatorType: "chat-ended" } });
            }
            return items;
        }

        // Preprocess important dates
        const autopilotActiveAt = toDate(chatTask.customerAbsenceStartedAt);
        const isAutopilotActive = chatTask.isCustomerAbsent;
        const agentJoinedAt = toDate(chatTask.assignedAt);

        let autopilotSeparatorInserted = false;
        let agentJoinedSeparatorInserted = false;

        // Process message list first while checking for separators
        // This allows us to insert separators at the correct points in the list
        for (let i = 0; i < messages.length; i++) {
            const msg = messages[i];
            const msgDate = toDate(msg.dateCreated);

            // Insert Autopilot separator if needed
            if (
                isAutopilotActive &&
                autopilotActiveAt &&
                msgDate &&
                !autopilotSeparatorInserted &&
                msgDate >= autopilotActiveAt
            ) {
                items.push({ type: "separator", data: { chatTask, separatorType: "autopilot-active" } });
                autopilotSeparatorInserted = true;
            }
            // Insert Agent Joined separator if needed
            if (agentJoinedAt && msgDate && !agentJoinedSeparatorInserted && msgDate >= agentJoinedAt) {
                items.push({ type: "separator", data: { chatTask, separatorType: "agent-joined" } });
                agentJoinedSeparatorInserted = true;
            }
            // finally add the message itself
            items.push({ type: "message", data: msg, index: i });
        }

        // If no message after autopilot, show at end
        if (isAutopilotActive && autopilotActiveAt && !autopilotSeparatorInserted) {
            items.push({ type: "separator", data: { chatTask, separatorType: "autopilot-active" } });
        }
        // If no message after join, show at end
        if (agentJoinedAt && !agentJoinedSeparatorInserted) {
            items.push({ type: "separator", data: { chatTask, separatorType: "agent-joined" } });
        }

        // Add typing indicator if customer is typing
        if (isCustomerTyping) {
            items.push({
                type: "typing",
                data: {
                    sessionId,
                    customerName,
                    isLastCustomerMessage: messages[messages.length - 1]?.senderType === "customer",
                },
            });
        }

        // Add chat ended separator if in wrapping state
        if (chatTask.status === "wrapping") {
            items.push({ type: "separator", data: { chatTask, separatorType: "chat-ended" } });
        }

        return items;
        // autopilotState is needed as a dependency trigger even though not directly used to ensure the most up to date autopilot store is used.
        // chatTask.isCustomerAbsent and chatTask.customerAbsenceStartedAt are getters that read from the store.
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, chatTask, isCustomerTyping, sessionId, customerName, autopilotState]);
}
